import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Card, CardContent } from '@mui/material';
import { DirectionsRun as RunIcon, Favorite as HealthIcon } from '@mui/icons-material';
import { AuthProvider, useAuth } from './context/AuthContext';
import AuthPage from './components/auth/AuthPage';
import AppLoadingBar from './components/common/AppLoadingBar';
import Dashboard from './components/dashboard/Dashboard';
import ProfilePage from './components/profile/ProfilePage';
import PublicProfilePage from './components/profile/PublicProfilePage';
import PublicDashboardPage from './components/profile/PublicDashboardPage';
import HelpPage from './components/help/HelpPage';
import RoleSelectionDialog from './components/auth/RoleSelectionDialog';
import { teamService } from './services/teamService';
import { activityService } from './services/activityService';
import { hasMilesOrMinutes } from './utils/activityUtils';
import { ActivitySubmissionResult, ActivityFormData } from './types';
import HealthMetricsModal from './components/activities/HealthMetricsModal';
import RunningActivityModal from './components/activities/RunningActivityModal';
import './App.css';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const AppContent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, progress, needsRoleSelection, updateUserRole } = useAuth();
  const [userTeamId, setUserTeamId] = useState<string | null>(null);
  const [showHealthMetricsModal, setShowHealthMetricsModal] = useState(false);
  const [showRunningActivityModal, setShowRunningActivityModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showChoiceDialog, setShowChoiceDialog] = useState(false);
  const [healthMetricsData, setHealthMetricsData] = useState<(Partial<ActivityFormData> & { _submissionDate?: string }) | undefined>(undefined);
  const [navigateToPage, setNavigateToPage] = useState<string | undefined>(undefined);
  const [activitiesRefreshTrigger, setActivitiesRefreshTrigger] = useState(0);

  // Consume one-time page query params (e.g. ?page=teams) then clear from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get('page');
    if (!page) return;

    if (page === 'teams') {
      setNavigateToPage('teams');
      // One-shot navigation trigger: keep long enough for Dashboard to consume it,
      // then clear so future app loads use normal defaults.
      setTimeout(() => setNavigateToPage(undefined), 500);
    }

    params.delete('page');
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  // Get user's team ID
  useEffect(() => {
    const getUserTeam = async () => {
      // Only run this when user is fully authenticated and role selection is complete
      if (user && !needsRoleSelection) {
        try {
          // Get userId - try user.id first, fallback to Firebase Auth
          let userId = user.id;
          
          if (!userId) {
            console.warn('App.tsx: user.id is missing, attempting to get from Firebase Auth...');
            try {
              const { auth } = await import('./firebase/config');
              if (auth.currentUser?.uid) {
                userId = auth.currentUser.uid;
                console.log('App.tsx: Got userId from Firebase Auth:', userId);
              }
            } catch (error) {
              console.error('App.tsx: Error getting userId from Firebase Auth:', error);
            }
          }
          
          if (!userId) {
            console.warn('App.tsx: Cannot get userId, skipping team loading');
            return;
          }
          
          let teams = [];
          
          if (user.role === 'coach' || user.role === 'headCoach') {
            // First ensure coach/head coach is added as member of their own teams
            await teamService.ensureCoachMembership(userId);
            
            // For coaches and head coaches, check both coach-owned teams and team memberships
            const [coachTeams, memberTeams] = await Promise.all([
              teamService.getTeamsByCoach(userId),
              teamService.getTeamsByAthlete(userId)
            ]);
            
            // Combine and deduplicate teams
            const allTeams = [...coachTeams];
            memberTeams.forEach(memberTeam => {
              if (!allTeams.find(team => team.id === memberTeam.id)) {
                allTeams.push(memberTeam);
              }
            });
            
            teams = allTeams;
            console.log('Coach/Head Coach teams found:', coachTeams.length, 'member teams:', memberTeams.length, 'total:', teams.length);
          } else {
            teams = await teamService.getTeamsByAthlete(userId);
          }
          
          if (teams.length > 0) {
            setUserTeamId(teams[0].id);
            console.log('User team ID set to:', teams[0].id);
          } else {
            console.log('No teams found for user');
            setUserTeamId(null);
            // Clear the cached teamId from user object if no teams found
            if (user.teamId) {
              console.log('Clearing stale teamId from user object');
              user.teamId = undefined;
            }
          }
        } catch (error) {
          console.error('Error getting user team:', error);
          // On error, try to verify if user.teamId is still valid
          if (user.teamId) {
            console.log('Error fetching teams, attempting to verify cached teamId:', user.teamId);
            try {
              // Verify membership still exists
              const membershipCheck = await teamService.getTeamsByAthlete(user.id);
              if (membershipCheck.length > 0 && membershipCheck.find(t => t.id === user.teamId)) {
                console.log('Cached teamId verified, using as fallback');
                setUserTeamId(user.teamId);
              } else {
                console.log('Cached teamId invalid, clearing');
                user.teamId = undefined;
                setUserTeamId(null);
              }
            } catch (verifyError) {
              console.error('Could not verify teamId, clearing:', verifyError);
              user.teamId = undefined;
              setUserTeamId(null);
            }
          } else {
            setUserTeamId(null);
          }
        }
      }
    };

    getUserTeam();
  }, [user, needsRoleSelection]);

  const checkHealthMetricsStatus = useCallback(async () => {
    if (!user || !userTeamId) return;
    
    try {
      // Check if health metrics have been submitted today
      const today = activityService.getTodayDateString();
      const todayActivities = await activityService.getUserActivitiesForDate(user.id, today, userTeamId);
      
      // Check if any activity has health metrics (sleepScore > 0, hydrationLevel > 0, or nutritionLevel > 0)
      const hasHealthMetrics = todayActivities.some(activity => 
        activity.sleepScore > 0 || activity.hydrationLevel > 0 || activity.nutritionLevel > 0
      );
      
      // Check if there are any RUNNING activities for today (Run/Workout miles or Cross Training minutes)
      const hasRunningActivities = todayActivities.some(activity => hasMilesOrMinutes(activity));
      
      if (!hasHealthMetrics) {
        // No health metrics submitted today, show health metrics modal
        setShowHealthMetricsModal(true);
      }
      // Note: We don't show confirmation modal on app load - only when user clicks "Log Today's Activity"
      // The confirmation modal will be shown in handleChoiceRunActivity if needed
    } catch (error) {
      console.error('Error checking health metrics status:', error);
    }
  }, [user, userTeamId]); // Dependencies: user and userTeamId

  // Check health metrics status and show appropriate modal (for athletes, or coaches/head coaches with useAthleteWorkflow enabled)
  useEffect(() => {
    if (user && userTeamId) {
      const shouldUseAthleteWorkflow = 
        user.role === 'athlete' || 
        ((user.role === 'coach' || user.role === 'headCoach') && user.useAthleteWorkflow);
      
      if (shouldUseAthleteWorkflow) {
        checkHealthMetricsStatus();
      }
    }
  }, [user, userTeamId, checkHealthMetricsStatus]);


  const handleHealthMetricsSubmitted = (data: { sleepScore: number; hydrationLevel: number; nutritionLevel: number; _submissionDate: string }) => {
    console.log('Health metrics submitted:', data, 'for date:', data._submissionDate);
    setHealthMetricsData(data);
    // Trigger refresh of activities list
    setActivitiesRefreshTrigger(prev => prev + 1);
    setShowHealthMetricsModal(false);
    setShowRunningActivityModal(true);
  };

  const handleActivitySubmitted = (result: ActivitySubmissionResult, navigateToDashboard?: () => void) => {
    console.log('Activity submitted:', result);
    setShowRunningActivityModal(false);
    
    // Trigger refresh of activities list
    setActivitiesRefreshTrigger(prev => prev + 1);
    
    // Navigate to Daily Activity Dashboard after submission
    if (navigateToDashboard) {
      navigateToDashboard();
    } else {
      // Fallback: navigate directly if no callback provided
      setNavigateToPage('activities');
      setTimeout(() => setNavigateToPage(undefined), 100);
    }
    
    console.log('Activity submitted - points should be updated in database');
  };

  const handleConfirmAddAnother = () => {
    setShowConfirmationModal(false);
    setHealthMetricsData(undefined); // Adding another for today, clear stale data
    setShowRunningActivityModal(true);
  };

  const handleCancelAddAnother = () => {
    setShowConfirmationModal(false);
  };

  const handleLogActivity = useCallback(() => {
    // Show choice dialog to let user select between Run Activity or Health Metrics
    if (user && userTeamId) {
      setShowChoiceDialog(true);
    }
  }, [user, userTeamId]); // Dependencies: user and userTeamId

  const handleChoiceRunActivity = async () => {
    setShowChoiceDialog(false);
    setHealthMetricsData(undefined); // Opening Activity directly, clear any health metrics data
    
    // Check if there are already running activities for today
    if (user && userTeamId) {
      try {
        const today = activityService.getTodayDateString();
        const todayActivities = await activityService.getUserActivitiesForDate(user.id, today, userTeamId);
        
        // Check if there are any RUNNING activities for today (Run/Workout miles or Cross Training minutes)
        const hasRunningActivities = todayActivities.some(activity => hasMilesOrMinutes(activity));
        
        if (hasRunningActivities) {
          // There are running activities for today, show confirmation modal
          setShowConfirmationModal(true);
        } else {
          // No running activities yet, show running activity modal directly
          setShowRunningActivityModal(true);
        }
      } catch (error) {
        console.error('Error checking for existing activities:', error);
        // On error, just show the modal anyway
        setShowRunningActivityModal(true);
      }
    } else {
      setShowRunningActivityModal(true);
    }
  };

  const handleChoiceHealthMetrics = () => {
    setShowChoiceDialog(false);
    setShowHealthMetricsModal(true);
  };

  const handleDashboardActivitySubmitted = useCallback((navigateToProfile?: () => void) => {
    // Pass navigation callback if provided
    if (navigateToProfile) {
      navigateToProfile();
    }
    console.log('Activity submitted - triggering points refresh');
  }, []); // No dependencies needed

  const handleRoleSelected = async (role: 'coach' | 'athlete') => {
    try {
      await updateUserRole(role);
    } catch (error) {
      console.error('Failed to update user role:', error);
    }
  };

  if (loading) {
    return (
      <>
        <AppLoadingBar progress={progress} message="Loading Streaker..." />
        <Box sx={{ minHeight: '100vh', pt: '60px' }} />
      </>
    );
  }

  // Handle public routes first
  const currentPath = location.pathname;
  if (currentPath.startsWith('/help')) {
    return <HelpPage />;
  }
  if (currentPath.startsWith('/profile/')) {
    return (
      <Routes>
        <Route path="/profile/:userId/dashboard" element={<PublicDashboardPage />} />
        <Route path="/profile/:userId" element={<PublicProfilePage />} />
      </Routes>
    );
  }

  if (!user) {
    return <AuthPage />;
  }

  // Show role selection dialog for new Google users
  if (needsRoleSelection) {
    return (
      <RoleSelectionDialog
        open={true}
        onRoleSelected={handleRoleSelected}
        loading={loading}
      />
    );
  }

  return (
    <>
      <Dashboard 
        userTeamId={userTeamId}
        onActivitySubmitted={handleDashboardActivitySubmitted}
        onLogActivity={handleLogActivity}
        navigateToPage={navigateToPage}
        activitiesRefreshTrigger={activitiesRefreshTrigger}
      />
      
      {/* Health Metrics Modal */}
      {userTeamId && (
        <HealthMetricsModal
          open={showHealthMetricsModal}
          onClose={() => setShowHealthMetricsModal(false)}
          onHealthMetricsSubmitted={handleHealthMetricsSubmitted}
          teamId={userTeamId}
        />
      )}

      {/* Running Activity Modal */}
      {userTeamId && (
        <RunningActivityModal
          open={showRunningActivityModal}
          onClose={() => {
            setShowRunningActivityModal(false);
            setHealthMetricsData(undefined);
          }}
          onActivitySubmitted={handleActivitySubmitted}
          teamId={userTeamId}
          healthMetricsData={healthMetricsData}
        />
      )}

      {/* Confirmation Modal */}
      <Dialog open={showConfirmationModal} onClose={handleCancelAddAnother}>
        <DialogTitle>Add Another Activity?</DialogTitle>
        <DialogContent>
          <Typography>
            You've already logged an activity for today. Are you sure you'd like to add another running activity?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAddAnother} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmAddAnother} color="primary" variant="contained">
            Yes, Add Another
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activity Type Choice Dialog */}
      <Dialog 
        open={showChoiceDialog} 
        onClose={() => setShowChoiceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h5" textAlign="center">
            What would you like to log?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                },
                transition: 'all 0.2s ease-in-out'
              }}
              onClick={handleChoiceRunActivity}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <RunIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Activity
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Log your running activity, workout, cross training, or rest day
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                },
                transition: 'all 0.2s ease-in-out'
              }}
              onClick={handleChoiceHealthMetrics}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <HealthIcon sx={{ fontSize: 48, color: 'secondary.main' }} />
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Health Metrics
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Log your sleep, hydration, and nutrition scores
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChoiceDialog(false)} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
